/**
 * Rate-limit (HTTP 429) helpers — shared so every place in the app surfaces a
 * throttle the SAME friendly way ("cool down", not "failed").
 *
 * Backend rate limits come in two flavors:
 *  - Custom guards (CSV import, import-delete) return a full descriptive
 *    sentence with how long to wait → we show it verbatim.
 *  - The generic NestJS ThrottlerGuard returns "ThrottlerException: Too Many
 *    Requests" → we replace it with a clear, friendly cool-down message.
 *
 * After the axios interceptor (axiosConfig.ts) runs, a 429 is rejected as a
 * normalized object `{ statusCode: 429, code: '429', isRateLimit: true,
 * message, retryAfterSeconds? }`, so any of these checks will catch it.
 */

export function isRateLimitError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return typeof error === "string"
      ? /too many requests|rate ?limit|cool ?down/i.test(error)
      : false;
  }
  const e = error as Record<string, any>;
  if (e.isRateLimit) return true;
  const status = Number(
    e.statusCode ?? e.status ?? e.response?.status ?? e.response?.data?.statusCode,
  );
  if (status === 429) return true;
  if (e.code === "429" || e.code === 429) return true;
  if (e.retryAfterSeconds != null || e.response?.data?.retryAfterSeconds != null)
    return true;
  const msg = String(e.message ?? e.response?.data?.message ?? "");
  return /too many requests|rate ?limit|throttle|cool ?down|upload up to \d+/i.test(
    msg,
  );
}

export function getRetryAfterSeconds(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as Record<string, any>;
  const v = e.retryAfterSeconds ?? e.response?.data?.retryAfterSeconds;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

/**
 * Build the user-facing cool-down message. Uses the backend's descriptive
 * sentence when it has one; otherwise a friendly generic cool-down.
 */
export function getRateLimitMessage(error: unknown): string {
  const e = (error && typeof error === "object" ? error : {}) as Record<string, any>;
  const raw = e.message ?? e.response?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(" ") : typeof raw === "string" ? raw : "";
  const retry = getRetryAfterSeconds(error);

  // Custom guards already return a clear, descriptive sentence — show verbatim.
  if (
    msg &&
    /upload up to|you can (only|upload)|try again in|cool ?down|per (minute|hour|\d+ min)/i.test(
      msg,
    )
  ) {
    return msg;
  }

  // Generic ThrottlerException / empty → friendly cool-down.
  return `You're doing that too fast — please cool down${
    retry ? ` for about ${retry}s` : " for a moment"
  } and try again.`;
}
